/**
 * How a PO template's sections and columns become a page (z8nrz7f7dz). Pure,
 * so the rules both the editor preview and Print rely on are tested here.
 */

import type {
  PoTemplateColumn,
  PoTemplateLayout,
  PoTemplateSection,
  PurchaseOrderDetail,
  PurchaseOrderLine,
} from '@/types/purchaseOrders'
import { formatPoMoney, formatPoNumberValue, formatPoPercent } from './format'

const ADDRESS_SECTIONS = new Set<PoTemplateSection['key']>(['billTo', 'shipTo', 'supplier'])

export type PoSectionRow =
  | { kind: 'single'; section: PoTemplateSection }
  /** Consecutive address sections, side by side in equal columns. */
  | { kind: 'addresses'; sections: PoTemplateSection[] }
  /** Notes beside totals, as on a paper PO: notes left, totals right. */
  | { kind: 'summary'; notes: PoTemplateSection | null; totals: PoTemplateSection }

/**
 * The visible sections in print order, grouped into the rows of a classic PO:
 *
 * - each run of ADJACENT address sections (vendor, ship-to, bill-to) shares
 *   one row, so hiding one re-flows the rest evenly;
 * - totals and notes next to each other share a row, notes on the left —
 *   whichever order the template lists them in. Totals alone is a summary
 *   row too, so it stays right-aligned.
 *
 * `hasNotes` false drops an empty notes section rather than printing a
 * blank box.
 */
export function sectionRows(sections: PoTemplateSection[], hasNotes = true): PoSectionRow[] {
  const visible = sections.filter((s) => s.visible && (s.key !== 'notes' || hasNotes))
  const rows: PoSectionRow[] = []
  for (let i = 0; i < visible.length; i++) {
    const s = visible[i]
    if (ADDRESS_SECTIONS.has(s.key)) {
      const group = [s]
      while (i + 1 < visible.length && ADDRESS_SECTIONS.has(visible[i + 1].key)) group.push(visible[++i])
      rows.push({ kind: 'addresses', sections: group })
      continue
    }
    const next = visible[i + 1]
    if (s.key === 'totals') {
      const notes = next?.key === 'notes' ? next : null
      if (notes) i++
      rows.push({ kind: 'summary', notes, totals: s })
      continue
    }
    if (s.key === 'notes' && next?.key === 'totals') {
      rows.push({ kind: 'summary', notes: s, totals: next })
      i++
      continue
    }
    rows.push({ kind: 'single', section: s })
  }
  return rows
}

/** The template's accent colour, or the default navy when it is missing or not a #rrggbb. */
export const DEFAULT_ACCENT = '#1e3a5f'
export function accentColor(layout: Pick<PoTemplateLayout, 'branding'>): string {
  const c = layout.branding.accentColor
  return c && /^#[0-9a-fA-F]{6}$/.test(c) ? c : DEFAULT_ACCENT
}

/** Whether a section's field is shown. A field the template does not list is shown. */
export function fieldVisible(section: PoTemplateSection, key: string): boolean {
  return section.fields?.find((f) => f.key === key)?.visible ?? true
}

/** A section field's label, or `fallback` when the template does not rename it. */
export function fieldLabel(section: PoTemplateSection, key: string, fallback: string): string {
  return section.fields?.find((f) => f.key === key)?.label || fallback
}

/** Material attributes by material id, as `{ key: { value } }` maps. */
export type MaterialAttributes = Record<string, Record<string, unknown>>

/** Columns that hold money or figures, which print right-aligned. */
export function isNumericColumn(key: PoTemplateColumn['key']): boolean {
  return ['qty', 'unitPrice', 'discountPct', 'taxPct', 'lineTotal'].includes(key)
}

/** The attribute key behind an `attr:<key>` column, or null for a built-in one. */
export function attributeKey(key: PoTemplateColumn['key']): string | null {
  return key.startsWith('attr:') ? key.slice(5) : null
}

function attributeText(attrs: Record<string, unknown> | undefined, key: string): string {
  const entry = attrs?.[key]
  const value = entry && typeof entry === 'object' && 'value' in entry ? (entry as { value: unknown }).value : entry
  if (value == null || value === '') return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) return value.join(', ')
  return String(value)
}

/** One cell of the lines table, formatted in the template's locale. */
export function columnValue(
  column: PoTemplateColumn,
  line: PurchaseOrderLine,
  po: Pick<PurchaseOrderDetail, 'currency' | 'taxRatePct'>,
  locale: string,
  attributes: MaterialAttributes = {},
): string {
  const attr = attributeKey(column.key)
  if (attr) return attributeText(attributes[line.materialId], attr)
  switch (column.key) {
    case 'lineNo':
      return String(line.lineNo)
    case 'articleNo':
      return line.articleNo ?? ''
    case 'supplierRef':
      return line.supplierRef ?? ''
    case 'description':
      return line.description ?? ''
    case 'qty':
      return formatPoNumberValue(line.qty, locale)
    case 'uom':
      return line.uom ?? ''
    case 'unitPrice':
      return formatPoMoney(line.unitPrice, po.currency, locale)
    case 'discountPct':
      return line.discountPct ? formatPoPercent(line.discountPct, locale) : ''
    case 'taxPct':
      return formatPoPercent(line.taxPct ?? po.taxRatePct ?? 0, locale)
    case 'lineTotal':
      return formatPoMoney(line.lineTotal, po.currency, locale)
    default:
      return ''
  }
}

/** The template a PO prints with: its own snapshot once sent, the current one before. */
export function layoutForPo(po: Pick<PurchaseOrderDetail, 'templateSnapshot'>, current: PoTemplateLayout | undefined) {
  return po.templateSnapshot ?? current ?? null
}
